import React from 'react';
import { getHomeCoverages, IProduct, Package } from '@kizi/comparator-core';
import { Div, Image, P } from '@kizi/core-components';
import { IconV2 } from '@kizi/shared-components';
import { parseAdditionalCoverageData } from './utils';
import _ from 'lodash';
import {
  Price,
  ProsCons,
  ProsConsItem,
  sendCustomEvent,
  Sustainability,
} from '../../../components';
import { formatPrice } from '../../global';
import { ComparisonSubmitButton } from '../../../components/comparison/ComparisonSubmitButton';
import { onClick } from '../../product';
import { handleProsNCons } from '../energy/helpers';
import { getFieldByCategory } from '../../../components/ProductNew/ExpandedPart/tabs/DetailsTab/Home';

const onApplyClick = async (service, data: IProduct) => {
  const interpreter = service.state.context.steps[4];
  if (data.affiliateUrl) {
    sendCustomEvent('affiliate-apply', {
      selectedProduct: data,
    });
    const overviewIndex = service._state.context.steps.findIndex(
      (step) => step.id === 'productComparison',
    );
    service.send([
      {
        type: 'GOTO',
        index: overviewIndex,
      },
    ]);
    const url = new URL(window.location.href);
    if (url.searchParams.has('affiliateUrl')) {
      return;
    }
    url.searchParams.set('step', 'overview');

    window.open(`${url.toString()}&affiliateUrl`);
  } else {
    onClick(interpreter, service, data, 6);
  }
};

export const getProductComparisonOverview = (
  data: IProduct,
  _labels,
  priceMode,
  additionalData,
) => {
  const filters = JSON.parse(additionalData.filters);
  const additionalCoverages = parseAdditionalCoverageData(filters);
  const {
    image,
    provider,
    homePackage,
    testScore,
    priceInfo,
    purchasableProduct,
    name,
  } = data;
  const { sellingPoints } = provider;
  const { discountIncluded, total } = priceInfo;
  const priceLabel = priceMode === 'month' ? 'maand' : 'jaar';
  const mainCoverage = (() => {
    switch (additionalData.coveragetype) {
      case '1':
        return 'Inboedel';
      case '2':
        return 'Opstal';
      case '3':
        return 'Inboedel + opstal';
      default:
        return '';
    }
  })();
  const getProsConsType = (sellingPointValue) => {
    switch (sellingPointValue) {
      case 'Yes':
        return 'pros';
      case 'No':
        return 'danger';
      default:
        return 'warning';
    }
  };
  const getFeatureTooltip = (sellingPointDescription) => {
    if (sellingPointDescription.toLowerCase().includes('opzegbaar')) {
      return 'De meeste woonverzekeringen kun je na een jaar opzeggen. Vind je dat te lang? Kies dan voor een verzekering die direct opzegbaar is.';
    } else if (sellingPointDescription.toLowerCase().includes('garantie')) {
      return 'De waarde van je inboedel of opstal wordt ingeschat op basis van de door jou ingevulde gegevens. Veel verzekeraars geven daarom een garantie tegen onderverzekering af. Als bij schade blijkt dat je onderverzekerd bent, vergoeden de verzekeraars de schade toch volledig.';
    }
    return '';
  };
  const ownRisk =
    homePackage?.policyPolicyDetails?.[0].coverageHomes?.[0].deductibleAmount;
  return [
    {
      content: (
        <Div display="flex" flexDirection="column" alignItems="center">
          <Div display="flex" variant="productLogoWrapper" mb={[2, null, 5]}>
            {image ? (
              <Image
                alt={`logo-${name}`}
                height="100%"
                src={image}
                width="100%"
              />
            ) : (
              <Div height={40} />
            )}
          </Div>
        </Div>
      ),
      isVisible: true,
      toggleable: false,
    },
    {
      content: <P>{mainCoverage}</P>,
      isVisible: true,
      toggleable: false,
    },
    {
      content: additionalCoverages.length && <Div>{additionalCoverages}</Div>,
      isVisible: true,
      toggleable: false,
    },
    {
      content: sellingPoints && (
        <Div>
          {sellingPoints.map((sellingPoint, index) => (
            <ProsConsItem
              key={index}
              text={sellingPoint.description}
              type={getProsConsType(sellingPoint.value)}
              tooltip={getFeatureTooltip(sellingPoint.description)}
              childVariant="featureItemComparison"
            />
          ))}
        </Div>
      ),
      isVisible: true,
      toggleable: false,
    },
    {
      content: ownRisk && <P>{formatPrice(ownRisk)}</P>,
      isVisible: true,
      toggleable: false,
    },
    {
      content: (
        <Sustainability
          score={String(testScore)}
          variant="sustainabilityRegular"
          iconName="favicon"
        />
      ),
      isVisible: true,
      toggleable: false,
    },
    {
      content: (
        <Price
          discount={discountIncluded}
          label={`per ${priceLabel}`}
          value={total}
          variant="priceBig"
        />
      ),
      isVisible: true,
      toggleable: false,
    },
    {
      content: data.purchasableProduct ? (
        <Div display="flex" justifyContent="center">
          <ComparisonSubmitButton
            data={data}
            fieldName="overview.selectedProduct"
            nextIndex={5}
            onSubmitFn={onApplyClick}
            isProductEnabled={purchasableProduct}
          />
        </Div>
      ) : (
        <Div display="flex" justifyContent="center">
          <Div>Geen samenwerking met verzekeraar</Div>
        </Div>
      ),
      isVisible: true,
      toggleable: false,
    },
  ];
};

export const getProductComparisonAbout = (data: IProduct) => {
  const { provider, prosAndCons, characteristics } = data;
  const { description } = provider;

  const allowed = ['title1', 'title2'];
  const pros = handleProsNCons(prosAndCons?.pros, allowed);
  const cons = handleProsNCons(prosAndCons?.cons, allowed);

  const communication = characteristics?.find(
    (char) => char.label === 'Communicatie',
  );
  const polis = characteristics?.find((char) => char.label.includes('polis'));

  return [
    {
      content: <P>{description}</P>,
      isVisible: true,
      toggleable: false,
    },
    {
      content: prosAndCons && (
        <Div display="flex" flexDirection="column">
          {pros && <ProsCons data={pros} onlyPros />}
          {cons && <ProsCons data={cons} onlyCons />}
        </Div>
      ),
      isVisible: true,
      toggleable: false,
    },
    {
      content: communication && <P>{communication.value}</P>,
      isVisible: true,
      toggleable: false,
    },
    {
      content: polis && <P>{polis.value}</P>,
      isVisible: true,
      toggleable: false,
    },
  ];
};

export const getProductComparisonFees = (data: IProduct) => {
  const { fees = [] } = data;
  const additionalFees = [
    ...getFieldByCategory(fees, 'GLASS'),
    ...getFieldByCategory(fees, 'OUTDOOR'),
  ];
  const contentFees = getFieldByCategory(fees, 'CONTENT');
  const homeFees = getFieldByCategory(fees, 'HOME');

  return [
    ...[...contentFees, ...homeFees, ...additionalFees].map((fee) => ({
      content: fee.valueIcon ? (
        <IconV2 name={fee.value as string} variant="successBig" />
      ) : (
        <P>{fee.value || '-'}</P>
      ),
      isVisible: true,
      toggleable: false,
    })),
  ];
};

const labelsPremium = {
  'Inboedel- en Woonhuisverzekering':
    'customProperties.buildingHomeCombinationTotalInstallmentAmount',
  Inboedelverzekering: 'coverageHomes.totalInstallmentAmount',
  Woonhuisverzekering: 'coverageBuildings.totalInstallmentAmount',
  Aansprakelijkheidsverzekering: 'coverageLiabilities.totalInstallmentAmount',
  Administratiekosten: 'installmentAmountRenewalCharges',
  total: 'totalInstallmentAmount',
  'Eenmalige poliskosten van de verzekeraar': 'oneOffCostsAmount',
};

const showValueWhenZero = ['Eenmalige poliskosten van de verzekeraar'];

export const getProductComparisonPremium = (data: IProduct) => {
  const { homePackage } = data;
  const coverages = getHomeCoverages(homePackage as Package);
  return [
    ...Object.keys(labelsPremium)
      .map((label) => {
        const valueSource = labelsPremium[label];
        const value = _.get(
          valueSource.startsWith('coverage') ? coverages : homePackage,
          valueSource,
        );
        return {
          label,
          value,
        };
      })
      .filter(
        (field) =>
          Number(field.value) || showValueWhenZero.includes(field.label),
      )
      .map((item) => {
        return {
          content: <P>{formatPrice(Number(item.value))}</P>,
          isVisible: true,
          toggleable: false,
        };
      }),
  ];
};

export const homeHelpers = {
  overview: getProductComparisonOverview,
  about: getProductComparisonAbout,
  fees: getProductComparisonFees,
  premium: getProductComparisonPremium,
};
